import { useParams, Navigate, Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Seo } from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Reveal, MaskedLines } from "../components/motion";
import { AffiliateButton } from "../components/Affiliate";
import { CtaBanner } from "../components/CtaBanner";
import { NORD } from "../data/vpns";
import { getLearn, LEARN } from "../data/learn";

const LearnPage = () => {
  const { slug } = useParams();
  const page = getLearn(slug);
  if (!page) return <Navigate to="/learn" replace />;

  return (
    <Layout>
      <Seo title={page.metaTitle} description={page.metaDescription} type="article" />
      <section className="grain relative overflow-hidden bg-[#0F172A]">
        <div className="hero-grid absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-[#7EAAFF]">VPN Learning Center</p>
          <h1 className="font-display text-4xl font-black tracking-tighter text-white sm:text-5xl">
            <MaskedLines lines={[page.title]} />
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300">{page.intro}</p>
        </div>
      </section>

      <Breadcrumbs crumbs={[{ label: "Learn", to: "/learn" }, { label: page.label }]} />

      <div className="mx-auto max-w-3xl px-6 py-16 lg:px-8">
        <div className="article-body">
          {page.sections.map((s, i) => (
            <Reveal key={i} className="mb-12">
              <h2 className="mb-5 flex items-baseline gap-4 font-display text-2xl font-extrabold tracking-tight text-[#0F172A]">
                <span className="font-display text-sm font-black text-[#4687FF]">{String(i + 1).padStart(2, "0")}</span>
                {s.h}
              </h2>
              {s.p?.map((t, j) => <p key={j}>{t}</p>)}
              {s.list && <ul>{s.list.map((t, j) => <li key={j}>{t}</li>)}</ul>}
              {s.cta && (
                <div className="mt-6">
                  <AffiliateButton href={NORD.link} testId={`learn-cta-${i}`}>
                    Try NordVPN — Our #1 Pick
                  </AffiliateButton>
                </div>
              )}
            </Reveal>
          ))}

          {page.glossary && (
            <div className="space-y-6" data-testid="glossary-list">
              {page.glossary.map((g, i) => (
                <Reveal key={i}>
                  <div className="border-b border-slate-200 pb-6">
                    <h2 className="font-display text-lg font-extrabold tracking-tight text-[#0F172A]">{g.term}</h2>
                    <p className="mt-2 !mb-0 text-sm leading-relaxed text-slate-600">{g.def}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>

        <Reveal className="mt-12 border-t border-slate-200 pt-8">
          <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-slate-400">Keep Learning</h3>
          <div className="mt-4 flex flex-wrap gap-3">
            {page.related.map((r) => (
              <Link
                key={r.to}
                to={r.to}
                className="border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors duration-150 hover:border-[#4687FF] hover:text-[#4687FF]"
              >
                {r.label}
              </Link>
            ))}
            {LEARN.filter((l) => l.slug !== slug)
              .slice(0, 3)
              .map((l) => (
                <Link
                  key={l.slug}
                  to={`/learn/${l.slug}`}
                  className="border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors duration-150 hover:border-[#4687FF] hover:text-[#4687FF]"
                >
                  {l.label}
                </Link>
              ))}
          </div>
        </Reveal>
      </div>
      <CtaBanner />
    </Layout>
  );
};

export default LearnPage;
